import type maplibregl from 'maplibre-gl'
import { getDefaultOrder } from '../../lib/layer-tier-registry.js'

// Overlays live in a single stacking band between these two anchors: base layers sit below
// ANCHOR_OVERLAYS, drawn shapes sit above ANCHOR_SHAPES. Every overlay is ordered purely by
// its global `order` (registry default, or user override) — there are no per-category tiers.
export const ANCHOR_OVERLAYS = '__cairn_anchor_overlays'
export const ANCHOR_SHAPES = '__cairn_anchor_shapes'

const ALL_ANCHORS = [ANCHOR_OVERLAYS, ANCHOR_SHAPES]

const overlayGroups = new Map<string, string[]>()
const layerToGroup = new Map<string, string>()

export function ensureAnchors(map: maplibregl.Map): void {
  for (const id of ALL_ANCHORS) {
    if (!map.getLayer(id)) {
      map.addLayer({
        id,
        type: 'background',
        layout: { visibility: 'none' },
      })
    }
  }
}

/**
 * Find the live overlay layer immediately above `order` in the global stack, so a freshly
 * added layer slots into its correct default position without waiting for a full reorder.
 * Returns ANCHOR_SHAPES (top of the overlay band) if nothing sits above it.
 */
function anchorAboveOrder(map: maplibregl.Map, order: number): string {
  let beforeId = ANCHOR_SHAPES
  let bestOrder = Number.POSITIVE_INFINITY
  for (const [groupId, layerIds] of overlayGroups) {
    const groupOrder = getDefaultOrder(groupId)
    if (groupOrder > order && groupOrder < bestOrder) {
      const live = layerIds.find(id => map.getLayer(id))
      if (live) {
        bestOrder = groupOrder
        beforeId = live
      }
    }
  }
  return beforeId
}

export function addOverlayLayer(
  map: maplibregl.Map,
  spec: maplibregl.LayerSpecification,
  groupId?: string
): void {
  ensureAnchors(map)

  const order = groupId ? getDefaultOrder(groupId) : Number.POSITIVE_INFINITY
  map.addLayer(spec, anchorAboveOrder(map, order))

  if (groupId) {
    const existing = overlayGroups.get(groupId) ?? []
    if (!existing.includes(spec.id)) {
      existing.push(spec.id)
      overlayGroups.set(groupId, existing)
    }
    layerToGroup.set(spec.id, groupId)
  }
}

export function untrackOverlayLayer(layerId: string): void {
  const groupId = layerToGroup.get(layerId)
  if (!groupId) return
  const layers = overlayGroups.get(groupId)
  if (layers) {
    const idx = layers.indexOf(layerId)
    if (idx >= 0) layers.splice(idx, 1)
    if (layers.length === 0) overlayGroups.delete(groupId)
  }
  layerToGroup.delete(layerId)
}

export function clearOverlayTracking(): void {
  overlayGroups.clear()
  layerToGroup.clear()
}

export function reorderAllOverlays(
  map: maplibregl.Map,
  userOrderOverrides?: ReadonlyMap<string, number>
): void {
  ensureAnchors(map)

  // Single global stack: sort every tracked overlay group by its order (user override wins
  // over registry default) and re-stack them in one pass. Moving each group's layers before
  // ANCHOR_SHAPES in ascending order leaves the highest-order group just under the anchor —
  // i.e. on top — so the list order and the map z-order always agree.
  const groupIds = [...overlayGroups.keys()].sort((a, b) => {
    const orderA = userOrderOverrides?.get(a) ?? getDefaultOrder(a)
    const orderB = userOrderOverrides?.get(b) ?? getDefaultOrder(b)
    return orderA - orderB
  })

  for (const groupId of groupIds) {
    const layerIds = overlayGroups.get(groupId)
    if (!layerIds) continue
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId, ANCHOR_SHAPES)
      }
    }
  }
}

/** @deprecated Use reorderAllOverlays instead */
export function reorderOverlayGroups(map: maplibregl.Map, _topGroupIds: string[]): void {
  reorderAllOverlays(map)
}

export function addShapeLayer(map: maplibregl.Map, spec: maplibregl.LayerSpecification): void {
  ensureAnchors(map)
  map.addLayer(spec)
}
